// =========================
// Type + node helpers
// =========================

const INFRA = new Set(['Hovedmåler', 'Tavle', 'Undertavle']);

const isMainMeter = (t) => t === 'Hovedmåler';
const isDistributionBoard = (t) => t === 'Tavle';
const isSubBoard = (t) => t === 'Undertavle';
const isSEUType = (t) => !!t && !INFRA.has(t);

const isDataNode = (n) =>
  !!n && Array.isArray(n.dataStreams) && n.dataStreams.length > 0;

export const isResidualNode = (n) =>
  !!n && !isDataNode(n) && n.options?.specialNodeType !== null;

// =========================
// Tree helpers
// =========================

function toMapById(tree) {
  const map = new Map();
  tree.forEach((n) => map.set(n.energyTreeNodeId, n));
  return map;
}

const getAncestors = (nodeId, tree) => {
  const byId = toMapById(tree);
  const res = [];
  let cur = byId.get(nodeId);
  while (cur && cur.parentId != null) {
    const p = byId.get(cur.parentId);
    if (!p) break;
    res.push(p);
    cur = p;
  }
  return res;
};

const hasChild = (tree, parentId, predicate) =>
  tree.some(
    (n) => n.parentId === parentId && (predicate ? predicate(n) : true),
  );

// =========================
// Unified result builder
// =========================

function buildResult({ validMessages, warningMessages, errorMessages }) {
  const hasErrors = errorMessages.length > 0;
  const hasWarnings = warningMessages.length > 0;

  const message = hasErrors
    ? errorMessages[0]
    : hasWarnings
      ? warningMessages[0]
      : validMessages[0] || 'Gyldig konfiguration.';

  return {
    isValid: !hasErrors,
    message,
    validMessages,
    warningMessages,
    errorMessages,
    kind: hasErrors ? 'reject' : 'allow',
  };
}

// ======================================================
// validateAddNode({ targetType, parent, tree, dataStreams })
// ======================================================

export function validateAddNode({
  targetType,
  parent,
  tree,
  dataStreams,
  addingResidual,
}) {
  const validMessages = [];
  const warningMessages = [];
  const errorMessages = [];

  const isTargetMain = isMainMeter(targetType);
  const isTargetTavle = isDistributionBoard(targetType);
  const isTargetSub = isSubBoard(targetType);
  const isTargetSEU = isSEUType(targetType);

  const targetHasData = Array.isArray(dataStreams)
    ? dataStreams.length > 0
    : false;

  const parentIsMain = parent ? isMainMeter(parent.type) : false;
  const parentIsTavle = parent ? isDistributionBoard(parent.type) : false;
  const parentIsSEU = parent ? isSEUType(parent.type) : false;
  const parentHasData = parent ? isDataNode(parent) : false;
  const parentIsVirtualSEU = parentIsSEU && !parentHasData;
  const parentIsVirtualMain = parentIsMain && !parentHasData;

  const ancestors =
    parent && parent.energyTreeNodeId
      ? getAncestors(parent.energyTreeNodeId, tree)
      : [];
  const hasTavleInAncestors = ancestors.some((a) =>
    isDistributionBoard(a.type),
  );

  // --- Generic soft hint: normally add under existing node ---
  if (!parent) {
    warningMessages.push(
      'Nye noder oprettes normalt under en eksisterende node.',
    );
  }

  // --- HARD: Under SEU with data -> forbidden ---
  if (parent && isSEUType(parent.type) && isDataNode(parent)) {
    errorMessages.push(
      'Tilføjelse af node under en SEU med data er ikke tilladt og vil medføre fejl i forbrugsberegningen.',
    );
  }

  // --- HARD: Under residual -> forbidden ---
  if (parent && isResidualNode(parent)) {
    errorMessages.push('Residual noder bør ikke have underliggende noder.');
  }

  // --- Hovedmåler: only allowed under virtual Hovedmåler ---
  if (isTargetMain && parent) {
    const localParentIsVirtualMain =
      isMainMeter(parent.type) && !isDataNode(parent);
    if (!localParentIsVirtualMain) {
      errorMessages.push(
        'Hovedmåler kan kun tilføjes under en virtuel Hovedmåler (uden data). Foretages dette valg vil det sandsynligvis resultere i fejl i forbrugsberegningen.',
      );
    }
    {
      validMessages.push('Gyldig: Hovedmåler under virtuel Hovedmåler.');
    }
  }

  // --- Tavle: must be under Hovedmåler or virtual SEU ---
  if (isTargetTavle) {
    if (!parentIsMain && !parentIsVirtualSEU) {
      errorMessages.push(
        `Tavle må kun placeres under Hovedmåler eller virtuel SEU. (Forælder: ${parent?.type || 'Ukendt'})`,
      );
    } else {
      validMessages.push('Gyldig konfiguration af Tavle.');

      if (parentIsVirtualMain) {
        warningMessages.push(
          'Denne Tavle oprettes direkte under en virtuel Hovedmåler (uden data). Det er tilladt, men det anbefales at oprette en fysisk Hovedmåler under den virtuelle Hovedmåler og placere Tavlen derunder.',
        );
      }
    }
  }

  // --- Undertavle: requires Tavle in lineage ---
  if (isTargetSub) {
    const hasTavleInLineage = parentIsTavle || hasTavleInAncestors;
    if (!hasTavleInLineage) {
      errorMessages.push(
        'Undertavle kræver en Tavle i hierarkiet. Foretages dette valg vil det sandsynligvis resultere i fejl i forbrugsberegningen.',
      );
    } else {
      validMessages.push('Gyldig: Undertavle under Tavle / i Tavle-gren.');
    }
  }

  // --- Soft: node under virtual SEU without own data ---
  if (parentIsVirtualSEU && !targetHasData) {
    warningMessages.push(
      'Denne node oprettes under en virtuel SEU uden data og har selv ingen datastrømme. Det er tilladt, men det anbefales at mindst én node i denne gren har datastreams for meningsfuld forbrugsberegning.',
    );
  }

  // --- Soft: virtual SEU being created (SEU without data) ---
  if (isTargetSEU && !targetHasData && !addingResidual) {
    warningMessages.push(
      'Denne node oprettes som en virtuel SEU uden datastrømme. Det er tilladt, men antager typisk at der senere tilføjes en SEU med data længere nede i hierarkiet.',
    );
  }

  if (isTargetTavle && dataStreams?.length === 0 && !addingResidual) {
    warningMessages.push(
      'Tavle noder bør normalt have tilknyttet datastrømme for at sikre korrekt forbrugsberegning.',
    );
  }

  // --- Fallback positive message ---
  if (
    validMessages.length === 0 &&
    warningMessages.length === 0 &&
    errorMessages.length === 0
  ) {
    validMessages.push('Gyldig konfiguration.');
  }

  return buildResult({
    validMessages,
    warningMessages,
    errorMessages,
  });
}

// ======================================================
// validateEditNode({ currentNode, targetType, tree, dataStreams })
// ======================================================

export function validateEditNode({
  currentNode,
  targetType,
  tree,
  dataStreams,
}) {
  if (!currentNode) {
    return buildResult({
      validMessages: [],
      warningMessages: [],
      errorMessages: ['Mangler den nuværende node.'],
    });
  }

  const validMessages = [];
  const warningMessages = [];
  const errorMessages = [];

  // ---------- Context ----------

  const idMap = toMapById(tree);
  const parent = currentNode.parentId ? idMap.get(currentNode.parentId) : null;
  const isRoot = currentNode.parentId == null;

  const currentType = currentNode.type;
  const targetNodeType = targetType || currentType;

  const currentIsSEU = isSEUType(currentType);

  const targetIsMain = isMainMeter(targetNodeType);
  const targetIsTavle = isDistributionBoard(targetNodeType);
  const targetIsSub = isSubBoard(targetNodeType);
  const targetIsSEU = isSEUType(targetNodeType);

  const currentHasData = isDataNode(currentNode);
  const targetHasData = Array.isArray(dataStreams)
    ? dataStreams.length > 0
    : currentHasData;

  const ancestors = getAncestors(currentNode.energyTreeNodeId, tree);
  const hasTavleInAncestors = ancestors.some((a) =>
    isDistributionBoard(a.type),
  );
  const hasMainInAncestors = ancestors.some((a) => isMainMeter(a.type));

  const parentIsMain = parent ? isMainMeter(parent.type) : false;
  const parentIsTavle = parent ? isDistributionBoard(parent.type) : false;
  const parentIsSEU = parent ? isSEUType(parent.type) : false;
  const parentHasData = parent ? isDataNode(parent) : false;
  const parentIsVirtualMain = parentIsMain && !parentHasData;

  const hasSEUChild = hasChild(tree, currentNode.energyTreeNodeId, (n) =>
    isSEUType(n.type),
  );
  const hasDataChild = hasChild(tree, currentNode.energyTreeNodeId, (n) =>
    isDataNode(n),
  );

  const hasAnyDataNodeChildrenBelow = (nodeId) => {
    return tree.some((n) => {
      let current = n;
      while (current) {
        if (current.energyTreeNodeId === nodeId) {
          return isDataNode(n);
        }
        current = current.parentId ? idMap.get(current.parentId) : null;
      }
      return false;
    });
  };

  // ============================================================
  // HARD RULES (set errorMessages)
  // These are the only things that should block the user.
  // ============================================================

  // 1) Root must always be Hovedmåler
  if (isRoot && !targetIsMain) {
    errorMessages.push('Roden skal altid være en Hovedmåler.');
  }

  // 2) Tavle must have parent of type Hovedmåler or virtual SEU
  if (targetIsTavle) {
    if (!parent) {
      errorMessages.push(
        'Tavle skal have en forælder, typisk en Hovedmåler eller virtuel SEU.',
      );
    } else {
      const parentIsAllowedForTavle =
        parentIsMain || (parentIsSEU && !parentHasData); // virtuel SEU

      if (!parentIsAllowedForTavle) {
        errorMessages.push(
          `Tavle må kun placeres under Hovedmåler eller virtuel SEU. (Forælder: ${parent.type})`,
        );
      }
    }
  }

  // 3) Undertavle must have at least one Tavle in lineage (parent or ancestor)
  if (targetIsSub) {
    const hasTavleInLineage = parentIsTavle || hasTavleInAncestors;
    if (!hasTavleInLineage) {
      errorMessages.push(
        'Undertavle kræver en Tavle i lineage. Uden dette vil forbrugsberegningen sandsynligvis være forkert.',
      );
    }
  }

  // 4) Virtual SEU that is parent to SEUs must NOT be given data
  //
  //    - Start: current node is SEU, has no data, has SEU children -> OK
  //    - Edit: adding data in that situation -> ERROR (this is your hard rule)
  //
  if (currentIsSEU && !currentHasData && targetHasData && hasSEUChild) {
    errorMessages.push(
      'Denne node er en virtuel SEU med underliggende SEU-noder. Det er ikke tilladt at tilføje datakilder til den, da en SEU med data ikke bør være overordnet andre SEU-noder.',
    );
  }

  // ============================================================
  // SOFT RULES (warnings) – allowed but not ideal
  // ============================================================

  // A) Non-root Hovedmåler with another Hovedmåler above and parent is NOT virtual main
  if (targetIsMain && !isRoot) {
    if (hasMainInAncestors && !parentIsVirtualMain) {
      warningMessages.push(
        'Der findes allerede en Hovedmåler højere oppe i samme gren. Sørg for at dette matcher den ønskede struktur.',
      );
    } else if (parentIsVirtualMain) {
      // Recommended pattern: child of virtual main -> valid
      validMessages.push(
        'Gyldig: Direkte barn af virtuel Hovedmåler kan repræsentere en fysisk Hovedmåler.',
      );
    } else if (!hasMainInAncestors) {
      // A secondary main in some branch without another main above
      warningMessages.push(
        'Denne node ændres til Hovedmåler uden en tydelig virtuel Hovedmåler som parent. Verificér at denne gren skal have sin egen Hovedmåler.',
      );
    }
  }

  // B) Tavle directly under virtual Hovedmåler -> allowed but warn
  if (targetIsTavle && parentIsVirtualMain) {
    warningMessages.push(
      'Denne Tavle er placeret direkte under en virtuel Hovedmåler (uden data). Det er tilladt, men det anbefales at have en fysisk Hovedmåler som barn af den virtuelle Hovedmåler og placere Tavlen derunder.',
    );
  }

  // C) Undertavle under Tavle (or in Tavle-lineage) -> explicitly mark as good
  if (targetIsSub) {
    const hasTavleInLineage = parentIsTavle || hasTavleInAncestors;
    if (hasTavleInLineage) {
      validMessages.push('Gyldig: Undertavle under Tavle / i Tavle-gren.');
    }
  }

  // D) SEU as leaf + virtual -> warn (allowed but not ideal)
  if (targetIsSEU) {
    const isTargetVirtualSEU = !targetHasData;
    // Virtual SEU with data-children but stays virtual -> warn
    if (isTargetVirtualSEU && hasDataChild) {
      // warningMessages.push(
      //   'En virtuel SEU uden egne datakilder med underliggende noder med data er tilladt, men kan gøre strukturen sværere at forstå. Overvej om data bør samles højere oppe eller om navngivning/struktur kan forbedres.',
      // );
      validMessages.push(
        'Gyldig: Virtuel SEU uden egne datakilder med underliggende noder med data.',
      );
    }

    if (
      !hasAnyDataNodeChildrenBelow(currentNode.energyTreeNodeId) &&
      isTargetVirtualSEU &&
      !isResidualNode(currentNode)
    ) {
      warningMessages.push(
        'En virtuel SEU uden datakilder som bladnode er normalt ikke ønsket. Overvej om denne SEU bør have data eller placeres i en anden struktur.',
      );
    }

    // SEU with data as parent of other SEUs (if we are NOT in the hard “virtual->data” transition case)
    if (
      targetHasData &&
      hasSEUChild &&
      !(currentIsSEU && !currentHasData && targetHasData && hasSEUChild)
    ) {
      warningMessages.push(
        'En SEU med data som overordnet for andre SEU-noder er tilladt, men ikke anbefalet. Overvej om struktur og dataplacering er hensigtsmæssig.',
      );
    }
  }

  // ============================================================
  // Fallback positive message
  // ============================================================

  if (
    validMessages.length === 0 &&
    warningMessages.length === 0 &&
    errorMessages.length === 0
  ) {
    validMessages.push('Gyldig konfiguration.');
  }

  return buildResult({
    validMessages,
    warningMessages,
    errorMessages,
  });
}

// ======================================================
// validateMoveNode({ currentNode, parent, tree })
// ======================================================

export function validateMoveNode({ currentNode, parent, tree }) {
  const validMessages = [];
  const warningMessages = [];
  const errorMessages = [];

  if (!currentNode || !parent) {
    return buildResult({
      validMessages: [],
      warningMessages: [],
      errorMessages: ['Mangler node eller destination.'],
    });
  }

  const targetType = currentNode.type;
  const parentType = parent.type;

  const targetIsMain = isMainMeter(targetType);
  const targetIsTavle = isDistributionBoard(targetType);
  const targetIsSub = isSubBoard(targetType);

  const parentIsMain = isMainMeter(parentType);
  const parentIsTavle = isDistributionBoard(parentType);
  const parentIsSEU = isSEUType(parentType);
  const parentHasData = isDataNode(parent);
  const parentIsVirtualSEU = parentIsSEU && !parentHasData;
  const parentIsVirtualMain = parentIsMain && !parentHasData;

  // --- HARD: cannot move under residual ---
  if (isResidualNode(parent)) {
    errorMessages.push('Residual noder kan ikke have underliggende noder.');
  }

  // --- HARD: cannot move under SEU with data ---
  if (parentIsSEU && parentHasData) {
    errorMessages.push('SEU med data bør ikke have underliggende noder.');
  }

  // --- Hovedmåler move: warn if not under main/virtual main ---
  if (targetIsMain && !parentIsMain) {
    warningMessages.push(
      'Hovedmåler bør normalt placeres som rod eller direkte under en virtuel Hovedmåler. Sørg for at denne flytning matcher den ønskede struktur.',
    );
  }

  // --- Tavle: must be under Hovedmåler eller virtuel SEU ---
  if (targetIsTavle && !(parentIsMain || parentIsVirtualSEU)) {
    errorMessages.push(
      'Tavle må kun placeres under Hovedmåler eller virtuel SEU.',
    );
  }

  // --- Undertavle: must have Tavle in lineage ---
  if (targetIsSub) {
    const ancestors = getAncestors(parent.energyTreeNodeId, tree);
    const hasTavle =
      parentIsTavle || ancestors.some((a) => isDistributionBoard(a.type));
    if (!hasTavle) {
      errorMessages.push('Undertavle kræver Tavle i lineage.');
    } else {
      validMessages.push('Gyldig: Undertavle under Tavle / i Tavle-gren.');
    }
  }

  // --- Soft: Tavle directly under virtual main -> warning ---
  if (targetIsTavle && parentIsVirtualMain) {
    warningMessages.push(
      'Denne Tavle flyttes direkte under en virtuel Hovedmåler (uden data). Det er tilladt, men det anbefales at have en fysisk Hovedmåler mellem disse.',
    );
  }

  // --- Fallback positive message ---
  if (
    validMessages.length === 0 &&
    warningMessages.length === 0 &&
    errorMessages.length === 0
  ) {
    validMessages.push('Gyldig flytning ift. hierarki.');
  }

  return buildResult({
    validMessages,
    warningMessages,
    errorMessages,
  });
}
